import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-confirm-modal.css?inline';
import '../pk-modal/pk-modal.js';
import '../pk-button/pk-button.js';

/**
 * `<pk-confirm-modal>` — confirmation gate for destructive actions.
 *
 * Composes on top of `<pk-modal>`. Two modes:
 *   - **Simple confirm** — when `confirm-text` is empty/absent, the action
 *     button is always enabled and the dialog behaves like a plain
 *     yes/no confirmation.
 *   - **Typed gate** — when `confirm-text` is set, the action button is
 *     disabled until the user types that exact string into the field.
 *     Used for hostname/tenant-name "are you absolutely sure" gates.
 *
 * Mirrors `<pk-modal>`'s open contract (boolean attribute + show/hide).
 *
 * Attributes:
 *   - `open`          — boolean; shows/hides the modal.
 *   - `title`         — dialog heading.
 *   - `lede`          — paragraph below the heading.
 *   - `confirm-text`  — string the user must type verbatim to enable the
 *                       action button. When empty, the gate is bypassed.
 *   - `action-label`  — action button text (default `Delete`).
 *   - `cancel-label`  — cancel button text (default `Cancel`).
 *   - `variant`       — `primary` | `danger` (default `danger`); colours the
 *                       action button.
 *
 * Events:
 *   - `confirmed` — fired when the action button is clicked while enabled.
 *   - `cancelled` — fired on cancel/backdrop/Escape/close X.
 */
export class PkConfirmModal extends HTMLElement {
    static readonly tagName = 'pk-confirm-modal';

    static get observedAttributes(): string[] {
        return ['open', 'title', 'lede', 'confirm-text', 'action-label', 'cancel-label', 'variant'];
    }

    private readonly _root: ShadowRoot;
    private _typed = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('pk-cancel', this._onModalClose);
    }

    disconnectedCallback(): void {
        this.removeEventListener('pk-cancel', this._onModalClose);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'open' && !this.hasAttribute('open')) {
            this._typed = '';
        }
        this.render();
    }

    /** Programmatically open the modal. */
    show(): void {
        this.setAttribute('open', '');
    }

    /** Programmatically close the modal. */
    hide(): void {
        this.removeAttribute('open');
    }

    private readonly _onModalClose = (e: Event): void => {
        e.stopPropagation();
        this._emitCancelled();
    };

    private readonly _onInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        this._typed = input.value;
        this.render();
    };

    private readonly _onConfirmClick = (): void => {
        if (!this._isUnlocked()) return;
        this.dispatchEvent(new CustomEvent('confirmed', { bubbles: true, composed: true }));
    };

    private readonly _onCancelClick = (): void => {
        this._emitCancelled();
    };

    private _emitCancelled(): void {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('cancelled', { bubbles: true, composed: true }));
    }

    private _isUnlocked(): boolean {
        const target = this.getAttribute('confirm-text') ?? '';
        if (!target) return true;
        return this._typed === target;
    }

    private template(): TemplateResult {
        const isOpen      = this.hasAttribute('open');
        const title       = this.getAttribute('title')        ?? 'Are you sure?';
        const lede        = this.getAttribute('lede')         ?? '';
        const confirmText = this.getAttribute('confirm-text') ?? '';
        const actionLabel = this.getAttribute('action-label') ?? 'Delete';
        const cancelLabel = this.getAttribute('cancel-label') ?? 'Cancel';
        const variantAttr = this.getAttribute('variant')      ?? 'danger';
        const variant     = variantAttr === 'primary' ? 'primary' : 'danger';
        // Spec § 3.4: wider modal when there is body content beyond the title.
        const size        = (lede || confirmText) ? 'medium' : 'small';
        const unlocked    = this._isUnlocked();

        return html`
            <style>${styles}</style>
            <pk-modal
                ?open=${isOpen}
                title=${title}
                size=${size}
                dismissible="escape"
                variant=${variant === 'danger' ? 'danger' : nothing}
            >
                ${lede ? html`<p class="lede">${lede}</p>` : ''}

                ${confirmText ? html`
                    <label class="gate-label" for="confirm-input">
                        Type
                        <code class="gate-target">${confirmText}</code>
                        to confirm
                    </label>
                    <input
                        id="confirm-input"
                        class="gate-input ${unlocked ? 'unlocked' : ''}"
                        type="text"
                        autocomplete="off"
                        spellcheck="false"
                        .value=${this._typed}
                        @input=${this._onInput}
                    />
                ` : ''}

                <pk-button
                    slot="secondary"
                    variant="ghost"
                    type="button"
                    @click=${this._onCancelClick}
                >${cancelLabel}</pk-button>
                <pk-button
                    slot="primary"
                    variant=${variant}
                    type="button"
                    ?disabled=${!unlocked}
                    @click=${this._onConfirmClick}
                >${actionLabel}</pk-button>
            </pk-modal>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkConfirmModal.tagName)) {
    customElements.define(PkConfirmModal.tagName, PkConfirmModal);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-confirm-modal': PkConfirmModal;
    }
}
